import {ChangeDetectorRef, Component, ElementRef, NgZone, OnInit, ViewChild} from "@angular/core";
import {MatDialog} from "@angular/material/dialog";
import {DomSanitizer, SafeUrl} from "@angular/platform-browser";
import {Router} from "@angular/router";
import {generateUuid} from "@app/models/functions/generate-uuid.function";
import {CommonState} from "@app/store/common/common.state";
import {
  EditTaskDialogComponent,
  EditTaskDialogComponentData,
} from "@doctor-panel-modules/patient-treatment-plan/components/edit/edit-task-dialog/edit-task-dialog.component";
import {
  EditTherapyDialogComponent,
  EditTherapyDialogData,
} from "@doctor-panel-modules/patient-treatment-plan/components/edit/edit-therapy-dialog/edit-therapy-dialog.component";
import {environment} from "@env/environment";
import {ModalController, ToastController} from "@ionic/angular";
import {Store} from "@ngxs/store";
import {
  AssistantAudioResponseModel,
  ClinicalDataType,
  GlamHelperService,
  TrecTherapy,
} from "@trec/api";
import {VideoMeetingActions} from "@video-meeting/store/video-meeting.actions";
import {AssistantInfoModalComponent} from "../assistant-info-modal/assistant-info-modal.component";
import {AssistantChoosePatientModalComponent} from "../assistant-shared-modal/assistant-choose-patient-modal/assistant-choose-patient-modal.component";
import {AssistantTextModalComponent} from "../assistant-text-modal/assistant-text-modal.component";
import {NewPatientModalComponent} from "../new-patient-modal/new-patient-modal.component";
import {AssistantDataService} from "../voice-assistant-data.service";
@Component({
  selector: "app-voice-assistant-feature",
  templateUrl: "./voice-assistant-feature.component.html",
  styleUrls: ["./voice-assistant-feature.component.scss"],
  standalone: false,
})
export class VoiceAssistantFeatureComponent implements OnInit {
  @ViewChild("audioPlayerRef", {static: false}) audioPlayerRef!: ElementRef<HTMLAudioElement>;

  showOverlay = false;
  assistantMessage: string | null = null;
  isOpen = false;
  isRecording = false;
  audioUrl: SafeUrl | null = null;
  mediaRecorder: MediaRecorder | null = null;
  audioChunks: Blob[] = [];
  structureId = localStorage.getItem("c3-curr-structure");
  shouldUpload: boolean = true;
  // Nuova flag: controlla se scaricare automaticamente l'audio generato
  shouldDownload: boolean = false;
  vocalMessage = "in ascolto";
  resultVA: AssistantAudioResponseModel | any;
  context: string | string[] | undefined;

  baseData: any = {
    type: "calendar/task/1",
    patient_id: "",
  };

  baseDataTherapies: any = {
    type: "therapy/therapy/1",
    patient_id: "",
  };

  metadata: any = {
    start_date: null,
    end_date: null,
    source: environment.appId,
    provenance: "medical",
    entry_date: null,
    doctor: {
      id: "",
      name: "",
    },
    structure: "",
    tags: [],
  };

  taskData: any = {
    data_type: "",
    alarm: {
      type: "notification",
      anticipation_value: 10,
      anticipation_unit: "minute",
    },
    notes: "",
    regular: {
      frequency: {},
      variable: {
        executions: [],
      },
      executions: [],
    },
  };

  therapiesData: any = {
    AI: false,
    drug: {},
    extra: {},
    notes: "",
    options: [],
    state: "active",
  };

  task = {
    TYPE: "calendar/task/1",
    ...this.baseData,
    data: this.taskData,
    metadata: {...this.metadata},
  };

  therapies = {
    TYPE: "therapy/therapy/1",
    ...this.baseDataTherapies,
    data: this.therapiesData,
    metadata: {...this.metadata},
  };

  constructor(
    private glamHelperService: GlamHelperService,
    private sanitizer: DomSanitizer,
    private cd: ChangeDetectorRef,
    private ngZone: NgZone,
    private router: Router,
    private modalController: ModalController,
    private assistantDataService: AssistantDataService,
    private dialog: MatDialog,
    private store: Store,
    private toastCtrl: ToastController,
  ) { }

  toggleShouldDownload(event: Event) {
    try {
      const input = event.target as HTMLInputElement;
      this.shouldDownload = !!input.checked;
      localStorage.setItem("voice-assistant-download-audio", this.shouldDownload ? "1" : "0");
    } catch (e) {
      console.warn("[toggleShouldDownload] could not persist preference", e);
    }
  }

  ngOnInit(): void {
    try {
      const stored = localStorage.getItem("voice-assistant-download-audio");
      if (stored !== null) {
        this.shouldDownload = stored === "1" || stored === "true";
      } else {
        this.shouldDownload = false;
      }
    } catch {
      this.shouldDownload = false;
    }
  }

  inactivityTimer: number | null = null;

  toggleAssistant() {
    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      this.startRecording();
    } else {
      this.clearInactivityTimer();
    }
  }

  startInactivityTimer() {
    this.clearInactivityTimer();
    this.inactivityTimer = window.setTimeout(() => {
      this.inactivityTimer = window.setTimeout(() => {
        this.stopRecording();
        this.isOpen = false;
        this.cd.detectChanges();
      }, 20000); // 20 seconds
    }, 20000); // 20 seconds
  }

  clearInactivityTimer() {
    if (this.inactivityTimer !== null) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
  }

  async startRecording() {
    try {
      /* Firefox specific audio constraints */
      const isFirefox = /firefox/i.test(navigator.userAgent);
      const audioSettings: MediaStreamConstraints = {
        audio: isFirefox
          ? {
            channelCount: 1,
            sampleRate: 16000,
            sampleSize: 16,
          }
          : true,
      };

      const options = {mimeType: "audio/webm;codecs=opus"};
      this.audioChunks = [];
      const stream = await navigator.mediaDevices.getUserMedia(audioSettings);
      this.mediaRecorder = new MediaRecorder(stream, options);
      this.isRecording = true;
      this.cd.detectChanges();

      // Audio analysis for VAD
      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);

      const bufferLength = analyser.fftSize;
      const dataArray = new Uint8Array(bufferLength);

      let silenceStart: number | null = null;
      const silenceThreshold = 0.01;
      const endSpeechSilence = 3000;
      const inactivitySilence = 10000;
      let hasSpoken = false;

      const detectSilence = () => {
        analyser.getByteTimeDomainData(dataArray);
        let sum = 0;
        for (let i = 0; i < bufferLength; i++) {
          const value = (dataArray[i] - 128) / 128;
          sum += value * value;
        }
        const rms = Math.sqrt(sum / bufferLength);
        const now = Date.now();

        if (rms < silenceThreshold) {
          if (silenceStart === null) {
            silenceStart = now;
          } else {
            const silenceDuration = now - silenceStart;
            if (silenceDuration > inactivitySilence) {
              this.stopRecording(false);
              return;
            }
            if (silenceDuration > endSpeechSilence && hasSpoken) {
              this.stopRecording(true);
              return;
            }
          }
        } else {
          hasSpoken = true;
          silenceStart = null;
        }

        if (this.isRecording) {
          requestAnimationFrame(detectSilence);
        }
      };

      requestAnimationFrame(detectSilence);

      this.mediaRecorder.ondataavailable = (event) => {
        this.audioChunks.push(event.data);
      };

      this.mediaRecorder.onstop = () => {
        audioContext.close();
        if (this.shouldUpload) {
          const audioBlob = new Blob(this.audioChunks, {type: "audio/webm"});
          const unsafeUrl = URL.createObjectURL(audioBlob);
          this.audioUrl = this.sanitizer.bypassSecurityTrustUrl(unsafeUrl);
          this.uploadAudio(audioBlob);
        }
        this.cd.detectChanges();
      };

      this.mediaRecorder.start();
    } catch (err) {
      console.error("Errore nell'avvio della registrazione:", err);
      this.isRecording = false;
      this.cd.detectChanges();
    }
  }

  stopRecording(upload: boolean = true) {
    this.isRecording = false;
    this.isOpen = false;
    this.clearInactivityTimer();
    this.shouldUpload = upload;

    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
      this.mediaRecorder.stop();
      setTimeout(() => {
        try {
          this.mediaRecorder?.stream.getTracks().forEach((track) => track.stop());
        } catch (e) {
          // ignore
        }
        this.mediaRecorder = null;
      }, 200);
    }
  }

  async openAssistantInfo() {
    this.isOpen = false;
    this.stopRecording(false);
    this.clearInactivityTimer();
    const modal = await this.modalController.create({
      component: AssistantInfoModalComponent,
      cssClass: "assistant-info-modal",
    });

    await modal.present();
  }

  uploadAudio(audioBlob: Blob) {
    const audioFile = new File([audioBlob], "recording.webm", {type: "audio/webm"});
    let isTask = false;
    let isChat = false;
    let isInfoText = false;

    this.showOverlay = true;
    this.isOpen = false;
    this.assistantMessage = "Elaborazione in corso...";

    if (!this.structureId) {
      try {
        const stored = localStorage.getItem("c3-curr-structure");
        this.structureId = stored ? stored : null;
      } catch {
        this.structureId = null;
      }
    }

    try {
      // Eseguire il download automatico solo se abilitato dall'utente
      if (this.shouldDownload) {
        this.triggerDownload(audioBlob, "recording.webm");
      }
    } catch (e) {
      console.warn("[uploadAudio] automatic download failed", e);
    }

    this.glamHelperService.getAudioByAssistantResponse(audioFile, this.structureId!).subscribe({
      next: (res: AssistantAudioResponseModel) => {
        this.resultVA = res;
        this.ngZone.run(() => {
          if (
            res?.assistantResponse.type === "NAVIGATION" &&
            res?.assistantResponse.content?.url &&
            res?.assistantResponse.content?.url != "home/doctor-panel/patients/___"
          ) {
            const url = res?.assistantResponse?.content?.url ?? "";
            let normalizedUrl = url.trim();
            if (normalizedUrl.startsWith("home/")) {
              normalizedUrl = normalizedUrl.replace(/^home\//, "");
            }
            this.router.navigate([normalizedUrl]);
          } else if (res?.assistantResponse.content?.url === "home/doctor-panel/patients/___") {
            this.openTaskModalChoosePatient(
              "medical-records",
              res,
              res?.assistantResponse.content?.url,
            );
          } else if (res?.assistantResponse.type === "DRUG") {
            this.assistantDataService.emitDrugResponse(res.assistantResponse.content);
            this.openTaskModalChoosePatient("treatment-plan", res.assistantResponse.content);
          } else if (res?.assistantResponse.type === "TASK") {
            this.assistantDataService.emitTaskResponse(res.assistantResponse.content);
            isTask = true;
          } else if (res?.assistantResponse.type === "CREATE_CHAT_MESSAGE") {
            isChat = true;
          } else if (
            res?.assistantResponse.type === "TEXT" ||
            res?.assistantResponse.type === "OUT_OF_CONTEXT"
          ) {
            isInfoText = true;
          } else if (res?.assistantResponse.type === "VIDEOMEETING") {
            this.openTaskModalChoosePatient("video-meeting", res);
          } else if (res?.assistantResponse.type === "PDF") {
            this.openTaskModalChoosePatient("pdf", res);
          } else if (res?.assistantResponse.type === "NEW_PATIENT") {
            try {
              const dialogRef = this.dialog.open(NewPatientModalComponent, {
                autoFocus: false,
                restoreFocus: false,
                maxWidth: "95vw",
                maxHeight: "90vh",
                panelClass: "dynamic-dialog",
              });

              // pass the payload to the component instance
              if (dialogRef.componentInstance) {
                (dialogRef.componentInstance as any).payload = res;
                // when the component emits closed, close the dialog with the result
                (dialogRef.componentInstance as any).closed.subscribe((r: any) => {
                  try {
                    dialogRef.close(r);
                  } catch (e) {
                    // ignore
                  }
                });
              }

              dialogRef.afterClosed().subscribe(async (result) => {
                //console.log('NewPatient Modal closed with result:', result);
                // result: { accepted: boolean; choice?: string | null }
                if (!result) {
                  return;
                }
                if (result.accepted) {
                  // user accepted: we can navigate to patient record or emit an event

                  const currentStructureId = this.store.selectSnapshot(
                    CommonState.currentStructureId,
                  )!;

                  //console.log('Fiscal Code selected:', result.choice);

                  // Validate the selected/final fiscal code before proceeding
                  const selectedChoice = result.choice;
                  if (!this.isValidFiscalCode(selectedChoice)) {
                    await this.presentToast("Codice fiscale non valido ❌", "danger");
                    dialogRef.close(null);
                    return;
                  }

                  // mark selectedChoice as confirmed
                  res.selectedChoice = "Si";
                  res.assistantResponse.content.fiscalCode = selectedChoice;
                  res.choicesMapping = {Si: selectedChoice};

                  try {
                    await this.glamHelperService.followUp(currentStructureId, res).toPromise();
                    await this.presentToast("Nuovo Paziente arruolato con successo ✅", "success");
                    dialogRef.close(res.assistantResponse.content.fiscalCode);
                    try {
                      await this.refreshPatientsList();
                    } catch (e) {
                      // ignore refresh errors
                    }
                  } catch (err) {
                    console.error("Errore durante l'invio:", err);
                    await this.presentToast(
                      "Errore durante l'arruolamento del nuovo paziente ❌",
                      "danger",
                    );
                    // if (err && err.status === 400) {
                    //   await this.presentToast('Codice fiscale non valido ❌', 'danger');
                    // } else {
                    // }
                    dialogRef.close(null);
                  }
                  return;
                } else {
                  // user cancelled
                }
              });
            } catch (e) {
              console.error("[NEW_PATIENT] modal open error", e);
            }
          }
          if (res?.assistantResponse.type === "TOGGLE") {
            this.openTaskModalChoosePatient("telemonitoring", res);
          } else {
            console.warn("[uploadAudio] Uso non consentito o tipo non gestito");
            this.cd.detectChanges();
            setTimeout(() => {
              this.assistantMessage = null;
              this.cd.detectChanges();
            }, 3000);
          }

          setTimeout(() => {
            this.showOverlay = false;
            this.assistantMessage = null;
            this.cd.detectChanges();
          }, 3000);

          if (isTask) {
            this.openTaskModalChoosePatient("task", res?.assistantResponse.content?.url);
          }

          if (isChat) {
            this.openTaskModalChoosePatient("chat", res);
          }

          if (isInfoText) {
            this.openPatientInfoModal(res);
          }
        });
      },
      error: (err) => {
        console.error("[uploadAudio] Errore:", err);
        if (err && err.status === 401) {
          //this.presentToast('Impossibile inviare un messaggio.Il medico non ha abilitato questa funzione per il paziente selezionato ❌', 'danger');
        } else {
          this.presentToast("Errore durante il processo di elaborazione dell'audio ❌", "danger");
        }

        this.ngZone.run(() => {
          //this.assistantMessage = 'Errore durante la richiesta. Riprova.';
          setTimeout(() => {
            this.showOverlay = false;
            this.assistantMessage = null;
            this.cd.detectChanges();
          }, 3000);
        });
      },
    });
  }

  // Trigger a browser download for a Blob.
  private triggerDownload(blob: Blob, filename: string) {
    const name = filename || "recording.webm";
    const anyWin = window as any;
    if (anyWin.navigator && anyWin.navigator.msSaveOrOpenBlob) {
      try {
        anyWin.navigator.msSaveOrOpenBlob(blob, name);
        return;
      } catch (e) {
        console.warn("[triggerDownload] msSaveOrOpenBlob failed", e);
      }
    }
    const url = window.URL.createObjectURL(blob);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    if (isSafari) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string | null;
        if (!result) {
          window.open(url, "_blank");
          setTimeout(() => window.URL.revokeObjectURL(url), 1000);
          return;
        }
        const newWindow = window.open(result, "_blank");
        if (!newWindow) {
          const a = document.createElement("a");
          a.href = url;
          a.target = "_blank";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      };
      reader.readAsDataURL(blob);
      return;
    }

    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download = name;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);

    let clicked = false;
    try {
      a.click();
      clicked = true;
    } catch (e) { }

    if (!clicked) {
      try {
        const ev = new MouseEvent("click", {
          view: window,
          bubbles: true,
          cancelable: true,
        });
        clicked = a.dispatchEvent(ev);
      } catch (e) { }
    }

    if (!clicked) {
      try {
        const newWin = window.open(url, "_blank");
        if (!newWin) {
          a.style.display = "inline-block";
          a.textContent = "Scarica registrazione";
          a.style.position = "fixed";
          a.style.left = "20px";
          a.style.top = "20px";
          a.style.zIndex = "10000";
        }
      } catch (e) { }
    }

    setTimeout(() => {
      try {
        if (a.parentNode) {
          document.body.removeChild(a);
        }
      } catch (e) { }
      try {
        window.URL.revokeObjectURL(url);
      } catch (e) { }
    }, 1500);
  }

  openTaskModalChoosePatient(dataTask: string, res?: any, url?: string) {
    const dialogRef = this.dialog.open(AssistantChoosePatientModalComponent, {
      autoFocus: false,
      restoreFocus: false,
      maxWidth: "95vw",
      maxHeight: "90vh",
      panelClass: "dynamic-dialog",
      data: this.resultVA,
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      const fetchUserdata = this.store.selectSnapshot(CommonState.loggedUserPersonalData);
      if (!result || !result.datapatient) {
        return;
      }

      if (dataTask === "task") {
        const storeContext = this.store.selectSnapshot(CommonState.patientContext);
        if (storeContext) {
          this.context = Array.isArray(storeContext) ? storeContext : [storeContext];
        } else {
          try {
            const stored = localStorage.getItem("c3-curr-context");
            this.context = stored ? stored : undefined;
          } catch {
            this.context = [];
          }
        }
        this.context = this.context || [];

        this.task.patient_id = result.datapatient.trecUserId;
        this.task.metadata.start_date = this.resultVA?.assistantResponse?.content?.start_date
          ? this.convertToMilliseconds(this.resultVA.assistantResponse.content.start_date)
          : "";
        this.task.metadata.end_date = this.resultVA?.assistantResponse?.content?.end_date
          ? this.convertToMilliseconds(this.resultVA.assistantResponse.content.end_date)
          : "";
        this.task.metadata.entry_date = this.resultVA?.assistantResponse?.content?.start_date
          ? this.convertToMilliseconds(this.resultVA.assistantResponse.content.start_date)
          : "";
        this.task.metadata.doctor.id = fetchUserdata?.trecUserId;
        this.task.metadata.doctor.name = `${fetchUserdata?.surname} ${fetchUserdata?.name}`;
        this.task.metadata.structure = this.store.selectSnapshot(CommonState.currentStructureId);
        this.task.metadata.tags = this.context;

        const taskKey = this.resultVA?.assistantResponse?.content?.task?.toUpperCase();
        if (taskKey && ClinicalDataType[taskKey as keyof typeof ClinicalDataType]) {
          this.task.data.data_type = ClinicalDataType[taskKey as keyof typeof ClinicalDataType];
        } else {
          console.warn(`Chiave '${taskKey}' non trovata in ClinicalDataType`);
        }

        this.task.data.notes = this.resultVA?.assistantResponse?.content?.note || "";

        if (this.resultVA?.assistantResponse?.content?.executions?.length) {
          this.resultVA.assistantResponse.content.executions =
            this.resultVA.assistantResponse.content.executions.map((exec: any) => ({
              ...exec,
              _id: generateUuid(),
            }));
        }

        this.task.data.regular.executions =
          this.resultVA?.assistantResponse?.content?.executions || [];

        const freqType = this.resultVA?.assistantResponse?.content?.frequency?.type;
        const freq = this.resultVA?.assistantResponse?.content?.frequency;
        if (!freqType) {
          this.task.data.regular.frequency = {type: "every_x_days", values: [1]};
        } else {
          this.task.data.regular.frequency = {
            type: freqType,
            values: freq.values && freq.values.length > 0 ? freq.values : [1],
          };
        }

        this.openTaskModal(this.context);
        return;
      }

      if (dataTask === "medical-records") {
        const trecUserId = result.datapatient.trecUserId;
        if (trecUserId) {
          this.router.navigateByUrl(
            "doctor-panel/patients/" +
            trecUserId +
            "/page/(patient-page:medical-records/page/(medical-records:personal-data))",
          );
        } else {
          console.error("Nessuna destinazione di navigazione disponibile");
          this.router.navigate(["/home"]);
        }
        return;
      }

      if (dataTask === "chat") {
        const structureId = this.store.selectSnapshot(CommonState.currentStructureId)!;
        res.selectedChoice = this.setSelectedChoiceFromValue(
          res.choicesMapping,
          result.datapatient.trecUserId,
        );
        try {
          await this.glamHelperService.followUp(structureId, res).toPromise();
          await this.presentToast("Messaggio inviato con successo ✅", "success");
          dialogRef.close(result.datapatient.trecUserId);
        } catch (err) {
          console.error("Errore durante l'invio:", err);
          if (err && err.status === 401) {
            await this.presentToast(
              "Impossibile inviare un messaggio.Il medico non ha abilitato questa funzione per il paziente selezionato ❌",
              "danger",
            );
          } else {
            await this.presentToast("Errore durante l'invio del messaggio ❌", "danger");
          }
          dialogRef.close(null);
        }
        return;
      }

      if (dataTask === "video-meeting") {
        const structureId = this.store.selectSnapshot(CommonState.currentStructureId)!;
        const obj = {
          trecUserId: "",
          isPatient: true,
          fiscalCode: "",
          idAssistito: "",
        };

        res.assistantResponse.content = res.assistantResponse.content || {};
        res.assistantResponse.content.simpleMeeting =
          res.assistantResponse.content.simpleMeeting || {};
        res.assistantResponse.content.simpleMeeting.creatorId = fetchUserdata?.trecUserId;
        res.assistantResponse.content.simpleMeeting.creatorName = `${fetchUserdata?.name} ${fetchUserdata?.surname}`;
        obj.trecUserId = result.datapatient.trecUserId;
        res.assistantResponse.content.simpleMeeting.participants = res.assistantResponse.content
          .simpleMeeting.participants || [obj];
        res.selectedChoice = this.setSelectedChoiceFromValue(
          res.choicesMapping,
          result.datapatient.trecUserId,
        );
        try {
          await this.glamHelperService.followUp(structureId, res).toPromise();
          await this.presentToast("VideoChiamata programmata con successo ✅", "success");

          const isPhysicianValue = this.store.selectSnapshot(CommonState.isPhysician);
          const trecUserId = this.store.selectSnapshot(CommonState.userId);

          this.store.dispatch(
            new VideoMeetingActions.FetchMeetings({
              idStruttura: structureId,
              isPhysician: isPhysicianValue,
              idUserTrec: trecUserId,
              skipCreatorPersonalData: true,
            }),
          );
          dialogRef.close(result.datapatient.trecUserId);
        } catch (err) {
          console.error("Errore durante l'invio:", err);
          await this.presentToast("Errore durante Programmazione della VideoChiamata ❌", "danger");
          dialogRef.close(null);
        }
        return;
      }

      if (dataTask === "pdf") {
        try {
          const structureId = this.store.selectSnapshot(CommonState.currentStructureId)!;
          if (res.choicesMapping && res.assistantResponse?.content?.matchingPatients?.length > 1) {
            res.selectedChoice = (this as any).setSelectedChoiceFromValue(
              res.choicesMapping,
              result.datapatient.trecUserId,
            );
          } else {
            let patientName = `${result.datapatient.name} ${result.datapatient.surname}`;
            res.selectedChoice = (this as any).setSelectedChoiceFromValue(
              res.choicesMapping,
              patientName,
            );
          }
          this.glamHelperService.followUp(structureId, res).subscribe({
            next: async (response) => {
              const base64Data = response?.assistantResponse?.content?.pdfContent;
              if (!base64Data) {
                await this.presentToast("PDF non disponibile", "danger");
                return;
              }

              try {
                const byteCharacters = atob(base64Data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                  byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const uint8Array = new Uint8Array(byteNumbers);
                const blob = new Blob([uint8Array], {type: "application/pdf"});

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = response?.assistantResponse?.content?.fileName || "document.pdf";
                link.click();
                window.URL.revokeObjectURL(url);

                await this.presentToast("PDF scaricato con successo! ✅", "success");
              } catch (e) {
                console.error("Errore durante la decodifica o il salvataggio del PDF:", e);
                await this.presentToast("Errore durante il download del PDF ❌", "danger");
              }
            },
            error: async (err) => {
              console.error("Errore durante l'invio:", err);
              await this.presentToast("Errore durante l'invio del PDF ❌", "danger");
            },
          });
        } catch (err) {
          console.error("Errore durante il download del PDF:", err);
          await this.presentToast("Errore durante l'invio del PDF ❌", "danger");
        }
        return;
      }

      if (dataTask === "treatment-plan") {
        //console.log('Preparing therapies data for VA...');
        const storeContext = this.store.selectSnapshot(CommonState.patientContext);
        if (storeContext) {
          this.context = Array.isArray(storeContext) ? storeContext : [storeContext];
        } else {
          try {
            const stored = localStorage.getItem("c3-curr-context");
            this.context = stored ? stored : undefined;
          } catch {
            this.context = [];
          }
        }
        this.context = this.context || [];

        this.therapies.data.AI = true;
        this.therapies.patient_id = result.datapatient.trecUserId;
        this.therapies.metadata.start_date = this.resultVA?.assistantResponse?.content?.startDate
          ? this.convertToMilliseconds(this.resultVA.assistantResponse.content.startDate)
          : "";
        this.therapies.metadata.end_date = this.resultVA?.assistantResponse?.content?.endDate
          ? this.convertToMilliseconds(this.resultVA.assistantResponse.content.endDate)
          : 86300027999999;
        //entry_date === data di inserimento della terapia
        // Se non è presente entry_date nell'AssistantResponse, impostiamo la data del giorno corrente
        // convertita in millisecondi (inizio giornata, ore 00:00).
        this.therapies.metadata.entry_date = this.resultVA?.assistantResponse?.content?.entryDate
          ? this.convertToMilliseconds(this.resultVA.assistantResponse.content.entryDate)
          : new Date().setHours(0, 0, 0, 0);
        this.therapies.metadata.doctor.id = fetchUserdata?.trecUserId;
        this.therapies.metadata.doctor.name = `${fetchUserdata?.surname} ${fetchUserdata?.name}`;
        this.therapies.metadata.structure = this.store.selectSnapshot(
          CommonState.currentStructureId,
        );
        this.therapies.metadata.tags = this.context;

        this.therapies.data.notes = this.resultVA?.assistantResponse?.content?.note || "";
        this.therapies.data.start_date = this.resultVA?.assistantResponse?.content?.startDate
          ? this.convertToMilliseconds(this.resultVA.assistantResponse.content.startDate)
          : "";
        this.therapies.data.end_date = this.resultVA?.assistantResponse?.content?.endDate
          ? this.convertToMilliseconds(this.resultVA.assistantResponse.content.endDate)
          : "";
        if (result.productCode) {
          // result.productCode can be a string id, an object, or an array. Normalize it.
          try {
            let pc: any = result.productCode;
            // If it's an array, take the first element
            if (Array.isArray(pc)) pc = pc[0];

            // If it's a primitive (string/number), create a minimal object
            if (typeof pc === "string" || typeof pc === "number") {
              this.therapies.data.drug = {_id: String(pc)};
            } else if (pc && typeof pc === "object") {
              const id =
                pc.productCode || pc.code || pc.id || pc._id || pc.uuid || pc.identifier || null;
              const activeIngredient =
                pc.activeIngredient || pc.active_ingredient || pc.ingredient || null;
              const drugObj: any = {
                ...pc,
                _id: id ? String(id) : undefined,
              };
              if (activeIngredient) {
                drugObj.active_ingredient = activeIngredient;
                // rimuoviamo la property originale per evitare duplicazioni
                if ("activeIngredient" in drugObj) {
                  delete drugObj.activeIngredient;
                }
                if (
                  "active_ingredient" in drugObj &&
                  drugObj.active_ingredient === activeIngredient &&
                  "activeIngredient" in drugObj
                ) {
                  // ensure no leftover
                  delete drugObj.activeIngredient;
                }
              }
              this.therapies.data.drug = drugObj;
            } else {
              // fallback: wrap into an object
              this.therapies.data.drug = {_id: String(pc)};
            }
            //console.log('Prepared therapy drug:', this.therapies.data.drug);
          } catch (e) {
            console.warn("[openTaskModalChoosePatient] could not normalize productCode", e);
            this.therapies.data.drug = {_id: String(result.productCode)};
          }
        } else {
          this.therapies.data.drug = {};
        }

        const target =
          this.resultVA?.assistantResponse?.content?.therapyType === "regular"
            ? "regular"
            : "variable";

        if (target === "regular") {
          this.therapies.data.variable = {assumptions: []};
        } else {
          this.therapies.data.regular = {
            frequency: {type: "at_need", values: []},
            assumptions: [
              {
                quantity: null,
                moment: null,
                time: null,
                unit: this.mapAssumptionUnitFromUserInput(
                  this.resultVA?.assistantResponse?.content?.userInput,
                ),
                day: null,
              },
            ],
          };
        }

        this.therapies.data[target] = {
          frequency: this.getFrequency(
            this.resultVA?.assistantResponse?.content?.frequency,
            this.resultVA?.assistantResponse?.content?.frequency?.type,
          ),
          assumptions: this.buildAssumptions(
            this.resultVA?.assistantResponse?.content?.assumptions,
            this.resultVA?.assistantResponse?.content?.frequency?.type,
            this.resultVA?.assistantResponse?.content?.therapyType,
            target === "variable" ? this.therapies.data.regular.assumptions[0]?.unit : null,
          ),
        };

        //console.log('Therapies prepared for VA:', this.therapies);
        let options = {
          therapy: this.therapies as unknown as TrecTherapy,
          insulinTherapy: false,
          oncoTherapy: false,
        };

        this.openTherapiesModal(options);

        return;
      }

      // (mapAssumptionUnitFromUserInput moved to class scope below)

      if (dataTask === "telemonitoring") {
        //console.log('Preparing telemonitoring follow-up for VA...');
        const structureId = this.store.selectSnapshot(CommonState.currentStructureId)!;
        res.patientName = `${result.datapatient.surname} ${result.datapatient.name}`;
        res.selectedChoice = this.setSelectedChoiceFromValue(
          res.choicesMapping,
          result.datapatient.trecUserId,
        );
        try {
          await this.glamHelperService.followUp(structureId, res).toPromise();
          await this.presentToast(
            "Impostazioni Telemonitoraggio aggiornate con successo ✅",
            "success",
          );
          try {
            await this.refreshPatientsList();
          } catch (e) {
            // ignore refresh errors
          }
          dialogRef.close(result.datapatient.trecUserId);
        } catch (err) {
          console.error("Errore durante l'invio:", err);
          await this.presentToast(
            "Errore durante l'aggiornamento delle impostazioni Telemonitoraggio ❌",
            "danger",
          );
          dialogRef.close(null);
        }
        return;
      }

      if (dataTask === "error") {
        res.message = "ERRORE";
      }
    });
  }

  convertToMilliseconds(dateString: string): number {
    if (!dateString || typeof dateString !== "string") return 0;
    const parts = dateString.split("/").map((p) => parseInt(p, 10));
    if (parts.length !== 3 || parts.some(isNaN)) return 0;
    const [day, month, year] = parts;
    const date = new Date(year, month - 1, day);
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
  }

  private mapAssumptionUnitFromUserInput(userInput: string | null): string | null {
    if (!userInput || typeof userInput !== "string") return null;
    const s = userInput.toLowerCase();

    // Prioritized patterns that include 'assunzione' or 'assunzioni' before the unit
    const prefixed: Array<[RegExp, string]> = [
      [/\bassunz?ion(?:e|i)\b[\s,:-]{0,6}\bapplicazion(?:e|i)\b/iu, "application"],
      [/\bassunz?ion(?:e|i)\b[\s,:-]{0,6}\bbombolett(?:a|e)\b/iu, "spray"],
      [/\bassunz?ion(?:e|i)\b[\s,:-]{0,6}\bspray\b/iu, "spray"],
      [/\bassunz?ion(?:e|i)\b[\s,:-]{0,6}\bbustin(?:a|e)\b/iu, "sachet"],
      [/\bassunz?ion(?:e|i)\b[\s,:-]{0,6}\bcerott(?:o|i)\b/iu, "patch"],
      [/\bassunz?ion(?:e|i)\b[\s,:-]{0,6}\bcialdin(?:o|i|a)\b/iu, "cachet"],
      [/\bassunz?ion(?:e|i)\b[\s,:-]{0,6}\bclism(?:i|o|a)\b/iu, "enema"],
      [/\bassunz?ion(?:e|i)\b[\s,:-]{0,6}\bcompres(?:sa|se)\b/iu, "tablet"],
      [/\bassunz?ion(?:e|i)\b[\s,:-]{0,6}\bcucchiain(?:o|i)\b/iu, "teaspoon"],
      [/\bassunz?ion(?:e|i)\b[\s,:-]{0,6}\bcucchiai(?:o)\b/iu, "spoon"],
      [/\bassunz?ion(?:e|i)\b[\s,:-]{0,6}\bdos(?:i|e)\b/iu, "dose"],
      [/\bassunz?ion(?:e|i)\b[\s,:-]{0,6}\bfial(?:e|i|a)\b/iu, "vial"],
      [/\bassunz?ion(?:e|i)\b[\s,:-]{0,6}\bfiltr(?:o|i)\b/iu, "filter"],
      [/\bassunz?ion(?:e|i)\b[\s,:-]{0,6}\bflaconcin(?:o|i)\b/iu, "small_vial"],
      [/\bassunz?ion(?:e|i)\b[\s,:-]{0,6}\bflacon(?:e|i)\b/iu, "bottle"],
      [/\bassunz?ion(?:e|i)\b[\s,:-]{0,6}\bgarz(?:a|e)\b/iu, "gauze"],
      [/\bassunz?ion(?:e|i)\b[\s,:-]{0,6}\bgocci(?:a|e)\b/iu, "drop"],
      [/\bassunz?ion(?:e|i)\b[\s,:-]{0,6}\bgranul(?:o|i)\b/iu, "granule"],
      [/\bassunz?ion(?:e|i)\b[\s,:-]{0,6}\blavagg(?:io|i)\b/iu, "washing"],
      [/\bassunz?ion(?:e|i)\b[\s,:-]{0,6}\blavand(?:a|e)\b/iu, "lavage"],
      [/\bassunz?ion(?:e|i)\b[\s,:-]{0,6}\blitr(?:o|i)\b/iu, "liter"],
      [/\bassunz?ion(?:e|i)\b[\s,:-]{0,6}\bmilligramm(?:o|i)\b/iu, "milligram"],
      [/\bassunz?ion(?:e|i)\b[\s,:-]{0,6}\bmillilitr(?:o|i)\b/iu, "milliliter"],
      [/\bassunz?ion(?:e|i)\b[\s,:-]{0,6}\bmisurin(?:o|i)\b/iu, "scoop"],
      [/\bassunz?ion(?:e|i)\b[\s,:-]{0,6}\bovul(?:o|i)\b/iu, "vaginal_suppository"],
      [/\bassunz?ion(?:e|i)\b[\s,:-]{0,6}\bpastigli(?:a|e)\b/iu, "pill"],
      [/\bassunz?ion(?:e|i)\b[\s,:-]{0,6}\bpuff\b/iu, "puff"],
      [/\bassunz?ion(?:e|i)\b[\s,:-]{0,6}\bsciacqu(?:o|i)\b/iu, "rinse"],
      [/\bassunz?ion(?:e|i)\b[\s,:-]{0,6}\bsuppost(?:a|e)\b/iu, "suppository"],
      [
        /\bassunz?ion(?:e|i)\b[\s,:-]{0,6}\bunit(?:à|a)?\s+di\s+insulin(?:a|e)?\b/iu,
        "unit_of_insulin",
      ],
      [/\bassunz?ion(?:e|i)\b[\s,:-]{0,6}\binsulin(?:a)?\b/iu, "unit_of_insulin"],
    ];

    for (const [re, val] of prefixed) {
      if (re.test(s)) return val;
    }

    // Fallback generic patterns (without requiring 'assunzione' prefix)
    const regexMap: Array<[RegExp, string]> = [
      [/\bapplicazion(?:e|i|i di)\b/iu, "application"],
      [/\bbombolett(?:a|e)\b/iu, "spray"],
      [/\bspray\b/iu, "spray"],
      [/\bbustin(?:a|e)\b/iu, "sachet"],
      [/\bcerott(?:o|i)\b/iu, "patch"],
      [/\bcialdin(?:o|i)\b/iu, "cachet"],
      [/\bclism(?:i|o|a)\b/iu, "enema"],
      [/\bcompres(?:sa|se|se?)\b/iu, "tablet"],
      // Match 'cucchiaino' before 'cucchiaio' to prefer teaspoon over spoon
      [/\bcucchiain(?:o|i)\b/iu, "teaspoon"],
      [/\bcucchiaio\b/iu, "spoon"],
      [/\bdos(?:i|e|a)\b/iu, "dose"],
      [/\bfial(?:e|i)\b/iu, "vial"],
      [/\bfiltr(?:o|i)\b/iu, "filter"],
      [/\bflaconcin(?:o|i)\b/iu, "small_vial"],
      [/\bflacon(?:e|i|i)\b/iu, "bottle"],
      [/\bgarz(?:a|e)\b/iu, "gauze"],
      [/\bgocc(?:a|e)\b/iu, "drop"],
      [/\bgranul(?:o|i)\b/iu, "granule"],
      [/\blavagg(?:io|i)\b/iu, "washing"],
      [/\blavand(?:a|e)\b/iu, "lavage"],
      [/\blitr(?:o|i)\b/iu, "liter"],
      [/\bmilligramm(?:o|i)\b/iu, "milligram"],
      [/\bmillilitr(?:o|i)\b/iu, "milliliter"],
      [/\bmisurin(?:o|i)\b/iu, "scoop"],
      [/\bovul(?:o|i)\b/iu, "vaginal_suppository"],
      [/\bpastigli(?:a|e)\b/iu, "pill"],
      [/\bpuff\b/iu, "puff"],
      [/\bsciacqu(?:o|i)\b/iu, "rinse"],
      [/\bsuppost(?:a|e|e?)\b/iu, "suppository"],
      [/\bunit(?:à|a)?\s+di\s+insulin(?:a|e)?\b/iu, "unit_of_insulin"],
      [/\binsulin(?:a)?\b/iu, "unit_of_insulin"],
    ];

    for (const [re, val] of regexMap) {
      if (re.test(s)) return val;
    }

    return null;
  }

  /**
   * Normalizza e restituisce un array di "assumptions" accettando diverse forme
   * in input (array, stringa JSON, stringa separata da virgole, singolo oggetto).
   * frequencyType è opzionale e può essere usata in futuro per scegliere valori di default.
   */
  private buildAssumptions(
    inputAssumptions: any,
    frequencyType?: string,
    therapyType?: string,
    variableUnitAssumption?: any,
  ): any[] {
    if (!inputAssumptions) return [];

    // Parse string inputs (JSON or CSV)
    if (typeof inputAssumptions === "string") {
      const raw = inputAssumptions.trim();
      if (!raw) return [];
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) inputAssumptions = parsed;
        else if (parsed && typeof parsed === "object") inputAssumptions = [parsed];
        else
          inputAssumptions = raw
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
      } catch (e) {
        inputAssumptions = raw
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }
    }

    // If it's an object/map, extract values or wrap
    if (typeof inputAssumptions === "object" && !Array.isArray(inputAssumptions)) {
      try {
        const vals = Object.values(inputAssumptions).filter((v) => v !== null && v !== undefined);
        inputAssumptions = vals.length > 0 ? vals : [inputAssumptions];
      } catch (e) {
        inputAssumptions = [inputAssumptions];
      }
    }

    if (!Array.isArray(inputAssumptions)) return [];

    // Only replace time: null -> time: '' when frequencyType === 'at_need'
    const replaceTimeRec = (obj: any) => {
      if (!obj || typeof obj !== "object") return;
      for (const k of Object.keys(obj)) {
        const v = obj[k];
        if (k === "time" && v === null && frequencyType === "at_need") {
          obj[k] = "";
        } else if (Array.isArray(v)) {
          v.forEach((el: any) => replaceTimeRec(el));
        } else if (v && typeof v === "object") {
          replaceTimeRec(v);
        }
      }
    };

    const normalized = inputAssumptions.map((item) => {
      if (item && typeof item === "object") {
        const cloned = Array.isArray(item) ? [...item] : {...item};
        replaceTimeRec(cloned);
        // ensure each object has a unique _id
        try {
          (cloned as any)._id = (cloned as any)._id ?? generateUuid();
        } catch (e) {
          // ignore if generateUuid unavailable
        }
        return cloned;
      }
      return item;
    });

    // If therapyType === 'variable', group assumptions into arrays by 'day' property
    if (therapyType === "variable") {
      const buckets: Record<number, any[]> = {};
      let maxIndex = 0;
      for (const it of normalized) {
        if (it && typeof it === "object") {
          // accept numeric or numeric-string day
          const rawDay = (it as any).day;
          const dayNum = Number(rawDay);
          // map day=1 -> index 0 (1-based day to 0-based index)
          const idx =
            Number.isFinite(dayNum) && dayNum >= 1 ? Math.max(0, Math.floor(dayNum) - 1) : 0;
          const cloned = Array.isArray(it) ? [...it] : {...it};
          // remove day property as requested
          if ("day" in cloned) delete (cloned as any).day;
          // ensure each object has a unique _id
          try {
            (cloned as any)._id = (cloned as any)._id ?? generateUuid();
          } catch (e) {
            // ignore
          }
          if (!buckets[idx]) buckets[idx] = [];
          buckets[idx].push(cloned);
          if (idx > maxIndex) maxIndex = idx;
        } else {
          // primitives or non-objects go to index 0
          if (!buckets[0]) buckets[0] = [];
          // wrap primitive into object with _id
          const wrapped = {value: it};
          try {
            (wrapped as any)._id = generateUuid();
          } catch (e) { }
          buckets[0].push(wrapped);
        }
      }

      // convert buckets to array of arrays from 0..maxIndex
      const result: any[] = [];
      for (let i = 0; i <= maxIndex; i++) {
        result[i] = buckets[i] ?? [];
      }
      // remove any remaining 'day' properties from items before returning
      for (let i = 0; i < result.length; i++) {
        if (Array.isArray(result[i])) {
          result[i] = result[i].map((it: any) => {
            if (it && typeof it === "object" && "day" in it) {
              const clone = {...it};
              delete clone.day;
              return clone;
            }
            return it;
          });
        }
      }

      // If a unit was provided for variable assumptions, enforce it on every item
      if (variableUnitAssumption != null) {
        for (let i = 0; i < result.length; i++) {
          if (Array.isArray(result[i])) {
            result[i] = result[i].map((it: any) => {
              if (it && typeof it === "object") {
                const clone = {...it};
                clone.unit = variableUnitAssumption;
                return clone;
              }
              return it;
            });
          }
        }
      }

      return result;
    }

    // remove 'day' property from normalized objects (regular therapy) before returning
    const cleaned = normalized.map((item) => {
      if (item && typeof item === "object" && "day" in item) {
        const clone = Array.isArray(item) ? [...item] : {...item};
        delete (clone as any).day;
        return clone;
      }
      return item;
    });

    return cleaned;
  }

  /**
   * Restituisce una copia normalizzata dell'oggetto frequency.
   * Accetta in input l'oggetto `frequency` e opzionalmente `frequencyType`.
   * Al momento questa funzione fa una copia difensiva e, se `frequencyType`
   * è presente e `frequency.type` non è definito, la imposta su quel valore.
   */
  private getFrequency(frequency: any, frequencyType?: string): any {
    const effectiveType = frequencyType || (frequency && frequency.type);
    // If no frequency object, return a base object (with empty values for day_of_week or at_need)
    if (!frequency || typeof frequency !== "object") {
      const base: any = effectiveType ? {type: effectiveType} : {};
      if (effectiveType === "day_of_week") base.values = [];
      if (effectiveType === "at_need") ((base.type = "at_need"), (base.values = []));
      return base;
    }

    // Shallow copy (wrap arrays into values property)
    const copy: any = Array.isArray(frequency) ? {values: [...frequency]} : {...frequency};
    if (effectiveType && !copy.type) copy.type = effectiveType;

    // If at_need requested, force type and empty values
    if (copy.type === "at_need") {
      copy.type = "at_need";
      copy.values = [];
      return copy;
    }

    // Handle day_of_week: normalize textual weekday names or numeric strings into numeric values 1..7
    if (copy.type === "day_of_week") {
      const dayMap: Record<string, number> = {
        monday: 1,
        tuesday: 2,
        wednesday: 3,
        thursday: 4,
        friday: 5,
        saturday: 6,
        sunday: 7,
      };

      const valuesSet = new Set<number>();

      const addCandidate = (cand: any) => {
        if (cand == null) return;
        if (typeof cand === "number" && Number.isFinite(cand)) {
          valuesSet.add(cand);
          return;
        }
        if (typeof cand === "string") {
          const s = cand.trim().toLowerCase();
          if (dayMap[s]) {
            valuesSet.add(dayMap[s]);
            return;
          }
          const parsed = parseInt(s, 10);
          if (!Number.isNaN(parsed)) valuesSet.add(parsed);
        }
      };

      // Preferred keys to extract weekday-like values
      const keysToCheck = ["values", "value", "day", "weekday", "name", "days"];
      for (const k of keysToCheck) {
        const v = copy[k];
        if (v === undefined) continue;
        if (Array.isArray(v)) v.forEach(addCandidate);
        else addCandidate(v);
      }

      // Fallback: scan shallow properties
      if (valuesSet.size === 0) {
        for (const k of Object.keys(copy)) {
          const v = copy[k];
          if (Array.isArray(v)) v.forEach(addCandidate);
          else addCandidate(v);
        }
      }

      // Keep only valid 1..7 values and sort
      const normalized = Array.from(valuesSet)
        .filter((n) => Number.isFinite(n) && n >= 1 && n <= 7)
        .sort((a, b) => a - b);
      copy.values = normalized;
    }

    return copy;
  }

  /**
   * Restituisce la "selected choice" corrispondente al valore passato.
   * Accetta come primo argomento oppure l'intero oggetto `res` (che contiene
   * la struttura delle scelte) oppure direttamente la struttura `choices`
   * (mappa o array). Se viene passato l'oggetto `res`, la funzione imposta
   * anche `res.selectedChoice` e ritorna il valore impostato.
   *
   * Esempi di possibili forme di `choices` gestite:
   * - { key1: 'Si', key2: 'No' }
   * - ['Si', 'No']
   * - [{ key: 'k1', value: 'Si' }, { key: 'k2', value: 'No' }]
   * - [{ k1: 'Si' }, { k2: 'No' }]
   *
   * Ritorna la chiave corrispondente quando possibile, altrimenti ritorna
   * il valore passato (fallback).
   */
  private setSelectedChoiceFromValue(resOrChoices: any, value: any): any {
    let resObj: any = null;
    let choices: any = null;

    // Detect if first arg is the full response object
    if (
      resOrChoices &&
      (resOrChoices.assistantResponse || resOrChoices.choices || resOrChoices.choicesMapping)
    ) {
      resObj = resOrChoices;
      choices =
        resObj.assistantResponse?.content?.choices ||
        resObj.choices ||
        resObj.choicesMapping ||
        null;
    } else {
      // Treat first arg as the choices structure itself
      choices = resOrChoices;
    }

    try {
      // If choices is an object map { key: value }
      if (choices && typeof choices === "object" && !Array.isArray(choices)) {
        for (const key of Object.keys(choices)) {
          if (String(choices[key]) === String(value)) {
            const selected = key;
            if (resObj) resObj.selectedChoice = selected;
            return selected;
          }
        }
      }

      // If choices is an array
      if (Array.isArray(choices)) {
        for (const item of choices) {
          // primitive match
          if (item === value || String(item) === String(value)) {
            const selected = item;
            if (resObj) resObj.selectedChoice = selected;
            return selected;
          }
          if (item && typeof item === "object") {
            if ("key" in item && "value" in item) {
              if (String(item.value) === String(value)) {
                const selected = item.key;
                if (resObj) resObj.selectedChoice = selected;
                return selected;
              }
            }
            // fallback: iterate object properties
            for (const k of Object.keys(item)) {
              if (String(item[k]) === String(value)) {
                const selected = k;
                if (resObj) resObj.selectedChoice = selected;
                return selected;
              }
            }
          }
        }
      }

      // No choices structure or no match found: fallback to the passed value
      if (resObj) resObj.selectedChoice = value;
      return value;
    } catch (e) {
      if (resObj) resObj.selectedChoice = value;
      return value;
    }
  }

  openTaskModal(context: any) {
    this.dialog.open(EditTaskDialogComponent, {
      width: "90%",
      minWidth: "720px",
      autoFocus: false,
      restoreFocus: false,
      data: {task: this.task, AI: true, context: context} as EditTaskDialogComponentData,
    });
  }

  async openTherapiesModal(options: any, event?: Event) {
    event?.stopImmediatePropagation();

    const context = this.store.selectSnapshot(CommonState.patientContext);

    const modal = await this.modalController.create({
      component: EditTherapyDialogComponent,
      componentProps: {
        data: {
          therapy: options.therapy || null,
          insulinTherapy: !!options.insulinTherapy,
          oncoTherapy: !!options.oncoTherapy,
          context: context,
          insertDefaults: {atLeastOneAssumptionNeeded: true},
        } as EditTherapyDialogData,
      },
      cssClass: "c3-modal-width-lg c3-modal-height-lg",
    });
    await modal.present();
  }

  openPatientInfoModal(res: any) {
    this.dialog.open(AssistantTextModalComponent, {
      width: "0%",
      minWidth: "720px",
      autoFocus: false,
      restoreFocus: false,
      data: {data: res},
    });
  }

  public async presentToast(message: string, color: "success" | "danger"): Promise<void> {
    const toast = await this.toastCtrl.create({
      message,
      duration: 3000,
      color,
      position: "bottom",
    });
    await toast.present();
  }

  /**
   * Refresh patients list in doctor panel with sensible defaults.
   * Uses dynamic import to avoid circular dependencies.
   */
  private async refreshPatientsList(options?: {
    page?: number;
    arruolato?: string;
    filtro?: string;
    pageSize?: number;
    idStruttura?: any;
    sortBy?: string;
    direction?: string;
  }) {
    // Only refresh when user is on the patients list page
    try {
      const currentUrl =
        this.router && (this.router as any).url ? (this.router as any).url : this.router.url;
      if (currentUrl !== "/doctor-panel/page") {
        return;
      }
    } catch (e) {
      // if router is unavailable, skip refresh
      return;
    }
    const page = options?.page ?? 0;
    const arruolato = options?.arruolato ?? "ARRUOLATI";
    const filtro = options?.filtro ?? "";
    const pageSize = options?.pageSize ?? 20;
    const idStruttura =
      options?.idStruttura ?? this.store.selectSnapshot(CommonState.currentStructureId);
    const sortBy = options?.sortBy ?? "NAME";
    const direction = options?.direction ?? "ASC";

    const actions = await import("@modules/doctor-panel/store/doctor-panel.actions");
    await this.store.dispatch(
      new actions.DoctorPanelActions.FetchPatientsList({
        page,
        tipo: arruolato,
        filtro,
        pageSize,
        idStruttura,
        sortBy,
        direction,
      }),
    );
  }

  /**
   * Verifica la validità del codice fiscale italiano, inclusi casi particolari:
   * - Nomi/cognomi brevi: presenza di 'X' come riempitivo
   * - Omocodia: lettere in posizioni numeriche (posizioni 6, 7, 9, 10, 12, 13, 14)
   * - Nati all'estero: posizione 12 'Z' + 3 cifre
   * - Stranieri: codice paese in posizioni 12-15
   * - Carattere di controllo
   */
  private isValidFiscalCode(cf: string): boolean {
    if (!cf || typeof cf !== "string") return false;
    cf = cf.trim().toUpperCase();
    if (cf.length !== 16) return false;
    if (!/^[A-Z0-9]+$/.test(cf)) return false;

    // Nomi/cognomi brevi: presenza di 'X' come riempitivo nelle prime 6 posizioni
    if (/^[A-Z]{3}X{2,}/.test(cf.substring(0, 6))) {
      // accetta, ma non è sufficiente per validità completa
    }

    // Omocodia: posizioni 6,7,9,10,12,13,14 possono essere lettere invece di numeri
    const omocodiaMap: Record<string, string> = {
      L: "0",
      M: "1",
      N: "2",
      P: "3",
      Q: "4",
      R: "5",
      S: "6",
      T: "7",
      U: "8",
      V: "9",
    };
    const omocodiaPositions = [6, 7, 9, 10, 12, 13, 14];
    let cfNorm = cf.split("");
    omocodiaPositions.forEach((pos) => {
      const c = cfNorm[pos];
      if (omocodiaMap[c]) cfNorm[pos] = omocodiaMap[c];
    });
    const cfNormStr = cfNorm.join("");

    // Nati all'estero: posizione 11 (12-esima) == 'Z' e posizioni 12-14 sono numeri
    if (cf[11] === "Z" && /^[0-9]{3}$/.test(cf.substring(12, 15))) {
      // valido per nati all'estero
    }

    // Stranieri: posizioni 11-14 (12-15) sono codice paese (es: Z404)
    if (/^[A-Z][0-9]{3}$/.test(cf.substring(11, 15))) {
      // valido per stranieri
    }

    // Carattere di controllo
    const checkChar = cf[15];
    if (!/^[A-Z]$/.test(checkChar)) return false;
    // Calcolo carattere di controllo
    const oddMap = {
      "0": 1,
      "1": 0,
      "2": 5,
      "3": 7,
      "4": 9,
      "5": 13,
      "6": 15,
      "7": 17,
      "8": 19,
      "9": 21,
      A: 1,
      B: 0,
      C: 5,
      D: 7,
      E: 9,
      F: 13,
      G: 15,
      H: 17,
      I: 19,
      J: 21,
      K: 2,
      L: 4,
      M: 18,
      N: 20,
      O: 11,
      P: 3,
      Q: 6,
      R: 8,
      S: 12,
      T: 14,
      U: 16,
      V: 10,
      W: 22,
      X: 25,
      Y: 24,
      Z: 23,
    };
    const evenMap = {
      "0": 0,
      "1": 1,
      "2": 2,
      "3": 3,
      "4": 4,
      "5": 5,
      "6": 6,
      "7": 7,
      "8": 8,
      "9": 9,
      A: 0,
      B: 1,
      C: 2,
      D: 3,
      E: 4,
      F: 5,
      G: 6,
      H: 7,
      I: 8,
      J: 9,
      K: 10,
      L: 11,
      M: 12,
      N: 13,
      O: 14,
      P: 15,
      Q: 16,
      R: 17,
      S: 18,
      T: 19,
      U: 20,
      V: 21,
      W: 22,
      X: 23,
      Y: 24,
      Z: 25,
    };
    let sum = 0;
    for (let i = 0; i < 15; i++) {
      const c = cfNormStr[i];
      sum += i % 2 === 0 ? oddMap[c] : evenMap[c];
    }
    const controlChar = String.fromCharCode("A".charCodeAt(0) + (sum % 26));
    if (checkChar !== controlChar) return false;

    return true;
  }
}
